import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { userApi } from '../config';
import { getPillarMeta, getSpecialityLabel } from '../utils/breadcrumb';

const PILLAR_COLORS = [
  'var(--forest)',
  'var(--indigo)',
  'var(--rose)',
  'var(--marigold-dark)',
  'var(--forest-dark)',
];

const ALL_SERVICE_TYPES = ['Regular Classes', 'One-on-One Consultation'];
const ALL_MODES = ['Online', 'Offline'];

type ServiceType = 'Regular Classes' | 'One-on-One Consultation';

interface ServiceRecord {
  id: string;
  title: string;
  description: string;
  thumbnail?: string | null;
  mode: string;
  pricingType?: string;
  price: number;
  startDate?: string;
  endDate?: string;
  duration?: string | number;
  durationUnit?: string;
  classType?: string | null;
  serviceType?: ServiceType | null;
  isBookable: boolean;
}

interface InstructorCard {
  id: string;
  mentorId: string;
  slug: string;
  name: string;
  role: string;
  city: string;
  modes: string[];
  modeLabel: string;
  serviceTypes: ServiceType[];
  serviceCount: number;
  rating: number;
  reviews: number;
  experience: string;
  bio: string;
  price: number;
  initials: string;
  color: string;
  avatar?: string | null;
  responseTime: string;
  services: ServiceRecord[];
  topServiceId?: string;
}

const normalizeText = (value: any) => String(value || '').trim();

const getDisplayName = (item: any) => {
  const pieces = [item?.firstname, item?.lastname].map(normalizeText).filter(Boolean);
  return normalizeText(item?.vendorName) || pieces.join(' ') || normalizeText(item?.name) || 'Instructor';
};

const getInitials = (name: string) => {
  const tokens = normalizeText(name).split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return 'MF';
  if (tokens.length === 1) return tokens[0].slice(0, 2).toUpperCase();
  return `${tokens[0][0] || ''}${tokens[1][0] || ''}`.toUpperCase();
};

const inferServiceType = (service: any): ServiceType | null => {
  const classType = normalizeText(service?.classType).toLowerCase();
  if (classType === 'recurring' || classType === 'both') return 'Regular Classes';
  if (classType === 'fixed') return 'One-on-One Consultation';
  return null;
};

const normalizeMode = (mode: any) => {
  const raw = normalizeText(mode).toLowerCase();
  if (!raw) return 'Online + Offline';
  if (raw === 'both' || raw === 'online + offline' || raw === 'offline + online') return 'Online + Offline';
  if (raw.includes('online') && raw.includes('offline')) return 'Online + Offline';
  if (raw.includes('offline')) return 'Offline';
  if (raw.includes('online')) return 'Online';
  return normalizeText(mode);
};

const getModeList = (serviceRecords: ServiceRecord[]) => {
  const modes = new Set<string>();
  serviceRecords.forEach((service) => {
    const normalized = normalizeMode(service.mode);
    if (normalized === 'Online + Offline') {
      modes.add('Online');
      modes.add('Offline');
    } else if (normalized) {
      modes.add(normalized);
    }
  });

  return Array.from(modes);
};

const getModeLabel = (modes: string[]) => {
  if (modes.length === 0) return 'Online + Offline';
  if (modes.includes('Online') && modes.includes('Offline')) return 'Online + Offline';
  return modes.join(' / ');
};

const getStableRating = (seed: string) => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) % 1000;
  }
  const rating = 4.5 + (hash % 6) * 0.1;
  return Math.min(5, Math.max(4.5, Number(rating.toFixed(1))));
};

const getPriceLabel = (price: number) => {
  if (!price || Number.isNaN(price)) return 'Free Demo';
  return `₹${price.toLocaleString('en-IN')}`;
};

const getServiceSummary = (services: ServiceRecord[]) => {
  const titles = services
    .map((service) => service.title)
    .filter(Boolean)
    .slice(0, 2);
  return titles.join(' · ');
};

const SpecialityListing: React.FC = () => {
  const { tag } = useParams<{ tag: string }>();
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [practitioners, setPractitioners] = useState<InstructorCard[]>([]);
  const [loading, setLoading] = useState(true);

  const [selectedServiceTypes, setSelectedServiceTypes] = useState<string[]>(ALL_SERVICE_TYPES);
  const [selectedModes, setSelectedModes] = useState<string[]>(ALL_MODES);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [priceLimit, setPriceLimit] = useState<number>(3000);
  const [minRating, setMinRating] = useState<number>(0);

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        setLoading(true);
        const encodedTag = tag ? encodeURIComponent(tag) : '';
        const res = await userApi.get<any>(`/vendors-by-category?category=${encodedTag}`);
        const fetchedData = Array.isArray(res.data?.data) ? res.data.data : [];

        const mapped: InstructorCard[] = fetchedData
          .map((vendor: any, vendorIdx: number) => {
            const latestClass = Array.isArray(vendor?.latestClass) ? vendor.latestClass : [];
            const services: ServiceRecord[] = latestClass
              .map((svc: any, svcIdx: number) => {
                const priceValue = Number(
                  svc?.price ??
                  svc?.classPrice?.session ??
                  svc?.classPrice?.monthly ??
                  svc?.classPrice?.yearly ??
                  0,
                );

                const serviceType = inferServiceType(svc);
                return {
                  id: String(svc?._id || `${vendor?._id || vendorIdx}-${svcIdx}`),
                  title: normalizeText(svc?.classTitle) || 'Session',
                  description: normalizeText(svc?.classDescription || svc?.description),
                  thumbnail: svc?.classThumbnail || svc?.thumbnail || null,
                  mode: normalizeMode(svc?.mode),
                  pricingType: normalizeText(svc?.pricingType) || undefined,
                  price: Number.isNaN(priceValue) ? 0 : priceValue,
                  startDate: svc?.classStartDate,
                  endDate: svc?.classEndDate,
                  duration: svc?.classDuration,
                  durationUnit: svc?.durationUnit,
                  classType: normalizeText(svc?.classType) || null,
                  serviceType,
                  isBookable: Boolean(serviceType),
                };
              })
              .filter((service: ServiceRecord) => Boolean(service.id));

            const city = normalizeText(
              vendor?.city ||
              (vendor?.location && typeof vendor.location === 'object' ? vendor.location.city : vendor?.location) ||
              vendor?.addressCity ||
              vendor?.address
            );
            const modes = getModeList(services);
            const bookableServices = services.filter((service) => service.isBookable && service.serviceType);
            const serviceTypes = Array.from(new Set(bookableServices.map((service) => service.serviceType))) as ServiceType[];
            const allPrices = bookableServices.map((service) => service.price).filter((price) => Number.isFinite(price) && price > 0);
            const primaryPrice = allPrices.length > 0 ? Math.min(...allPrices) : 0;
            const name = getDisplayName(vendor);
            const role = normalizeText(vendor?.role) || normalizeText(vendor?.subRole) || services[0]?.title || 'Yoga Instructor';
            const bio = normalizeText(vendor?.bio || vendor?.description || vendor?.about || services[0]?.description) || 'Experienced instructor dedicated to your well-being.';
            const experience = normalizeText(vendor?.experience) || 'Experienced';
            const rating = Number(vendor?.rating || vendor?.averageRating || getStableRating(`${name}-${vendorIdx}`));
            const reviews = Number(vendor?.reviews || vendor?.reviewCount || vendor?.ratingCount || (72 + vendorIdx * 6));
            const slug = normalizeText(vendor?.slug) || normalizeText(vendor?.mentorId) || normalizeText(vendor?._id) || name.toLowerCase().replace(/\s+/g, '-');
            const serviceCount = services.length || 0;

            return {
              id: normalizeText(vendor?._id) || `${slug}-${vendorIdx}`,
              mentorId: normalizeText(vendor?.mentorId),
              slug,
              name,
              role,
              city,
              modes,
              modeLabel: getModeLabel(modes),
              serviceTypes,
              serviceCount,
              rating,
              reviews,
              experience,
              bio,
              price: primaryPrice,
              initials: getInitials(name),
              color: PILLAR_COLORS[vendorIdx % PILLAR_COLORS.length],
              avatar: vendor?.profileIcon || vendor?.profileImage || null,
              responseTime: normalizeText(vendor?.responseTime) || 'Responds within an hour',
              services,
              topServiceId: services[0]?.id,
            };
          })
          .filter((card: InstructorCard) => Boolean(card.name));

        setPractitioners(mapped);
      } catch (err) {
        console.error('Error fetching vendors:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchVendors();
  }, [tag]);

  const availableCities = useMemo(
    () => Array.from(new Set(practitioners.map((p) => p.city).filter(Boolean))),
    [practitioners],
  );

  const availableServiceTypes = useMemo(
    () => Array.from(new Set(practitioners.flatMap((p) => p.serviceTypes))),
    [practitioners],
  );

  const filteredPractitioners = useMemo(() => {
    return practitioners.filter((p) => {
      if (selectedServiceTypes.length > 0) {
        if (p.serviceTypes.length > 0) {
          if (!p.serviceTypes.some((type) => selectedServiceTypes.includes(type))) {
            return false;
          }
        } else if (selectedServiceTypes.length < ALL_SERVICE_TYPES.length) {
          return false;
        }
      }

      if (selectedCities.length > 0 && !selectedCities.includes(p.city)) {
        return false;
      }

      if (p.price > priceLimit) {
        return false;
      }

      if (p.rating < minRating) {
        return false;
      }

      if (selectedModes.length > 0) {
        const hasMode = p.modes.some((mode) => selectedModes.includes(mode));
        if (!hasMode) {
          return false;
        }
      }

      return true;
    });
  }, [practitioners, selectedServiceTypes, selectedCities, priceLimit, minRating, selectedModes]);

  const toggleArrayItem = (setState: React.Dispatch<React.SetStateAction<string[]>>, item: string) => {
    setState((prev) => (prev.includes(item) ? prev.filter((value) => value !== item) : [...prev, item]));
  };

  const prettyTag = tag ? decodeURIComponent(tag) : 'Yoga Instructor';
  const prettyTagTitle = prettyTag.replace(/\b\w/g, (c) => c.toUpperCase());
  const listingTitle = prettyTagTitle.endsWith('Instructor')
    ? prettyTagTitle.replace(/Instructor$/, 'Instructors')
    : prettyTagTitle;
  const breadcrumbSpeciality = getSpecialityLabel(prettyTag);
  const breadcrumbPillar = getPillarMeta(breadcrumbSpeciality);

  const onCardKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, slug: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      navigate(`/public/instructor-profile/${slug}`, {
        state: {
          sourcePillar: breadcrumbPillar,
          sourceSpeciality: breadcrumbSpeciality,
        },
      });
    }
  };

  const pickBookService = (card: InstructorCard) => {
    const preferred = card.services.find((service) => service.serviceType && selectedServiceTypes.includes(service.serviceType));
    return preferred || card.services[0] || undefined;
  };

  return (
    <>
      <style>{`
        :root{
          --ink:#15302B;
          --ink-soft:#3B5750;
          --paper:#F5F0E3;
          --paper-2:#ECE4CF;
          --card:#FFFDF7;
          --marigold:#E6A93F;
          --marigold-dark:#C98726;
          --forest:#3F6652;
          --forest-dark:#2C4A3B;
          --indigo:#57628F;
          --rose:#B9576B;
          --line: rgba(21,48,43,0.14);
          --line-soft: rgba(21,48,43,0.08);
          --shadow: 0 12px 30px -14px rgba(21,48,43,0.26);
          --pillar: var(--forest);
          --pillar-dark: var(--forest-dark);
        }

        .sl-wrap{max-width:1240px;margin:0 auto;padding:0 32px;}
        @media (max-width:640px){.sl-wrap{padding:0 18px;}}

        .eyebrow{
          font-family:'IBM Plex Mono',monospace;
          font-size:12px;
          letter-spacing:0.14em;
          text-transform:uppercase;
          color:var(--pillar-dark);
          white-space:nowrap;
        }

        .crumbs{
          display:flex;
          align-items:center;
          flex-wrap:wrap;
          gap:8px;
          font-size:12.5px;
          color:var(--ink-soft);
          margin-bottom:14px;
        }
        .crumbs a{
          color:inherit;
          text-decoration:none;
        }
        .crumbs a:hover{
          color:var(--ink);
          text-decoration:underline;
        }

        .sl-btn{
          font-family:'Inter',sans-serif;
          font-weight:600;
          font-size:13.5px;
          padding:10px 18px;
          border-radius:100px;
          border:1px solid transparent;
          cursor:pointer;
          display:inline-flex;
          align-items:center;
          gap:7px;
          transition:transform .18s ease, box-shadow .18s ease, background .2s ease, border-color .2s ease;
          text-decoration:none;
        }
        .sl-btn-primary{
          background:var(--ink);
          color:var(--paper);
        }
        .sl-btn-primary:hover{
          transform:translateY(-2px);
          box-shadow:var(--shadow);
          background:var(--forest-dark);
        }
        .sl-btn-ghost{
          background:transparent;
          color:var(--ink);
          border-color:var(--line);
        }
        .sl-btn-ghost:hover{
          background:var(--paper-2);
        }

        .page-head{
          padding:18px 0 14px;
          border-bottom:1px solid var(--line);
        }
        .page-head-row{
          display:flex;
          justify-content:space-between;
          align-items:flex-end;
          flex-wrap:wrap;
          gap:16px;
        }
        .page-head h1{
          font-family:'Fraunces',serif;
          font-weight:600;
          font-size:clamp(26px,2.8vw,34px);
          line-height:1.05;
          margin:0 0 6px;
          letter-spacing:-0.01em;
        }
        .head-controls{
          display:flex;
          gap:10px;
          align-items:center;
          flex-wrap:wrap;
        }
        select.sortsel{
          font-family:'Inter',sans-serif;
          font-size:13.5px;
          font-weight:500;
          padding:10px 16px;
          border-radius:100px;
          border:1px solid var(--line);
          background:var(--card);
          color:var(--ink);
          cursor:pointer;
          outline:none;
        }
        .filter-toggle{display:none;}
        @media (max-width:900px){
          .filter-toggle{display:inline-flex;}
        }

        .listing-layout{
          display:grid;
          grid-template-columns:270px 1fr;
          gap:34px;
          padding:22px 0 80px;
          align-items:start;
        }
        @media (max-width:900px){
          .listing-layout{grid-template-columns:1fr;}
        }

        .sidebar{
          background:var(--card);
          border:1px solid var(--line);
          border-radius:20px;
          padding:24px;
          position:sticky;
          top:88px;
          box-shadow:0 8px 22px -20px rgba(21,48,43,0.2);
        }
        .sidebar h3{
          font-size:12px;
          font-family:'IBM Plex Mono',monospace;
          text-transform:uppercase;
          letter-spacing:.08em;
          color:var(--ink-soft);
          margin-bottom:14px;
          font-weight:600;
        }
        .filter-group{margin-bottom:26px;}
        .filter-group:last-child{margin-bottom:0;}
        .filter-row{
          display:flex;
          align-items:center;
          justify-content:space-between;
          font-size:14px;
          padding:8px 0;
          cursor:pointer;
          color:var(--ink);
        }
        .filter-row span{
          display:flex;
          align-items:center;
          gap:10px;
        }
        .filter-row input{
          accent-color:var(--pillar);
          width:16px;
          height:16px;
        }
        .filter-row .fc{
          color:var(--ink-soft);
          font-size:12px;
          font-family:'IBM Plex Mono',monospace;
        }
        .price-range{
          display:flex;
          align-items:center;
          justify-content:space-between;
          font-size:13px;
          color:var(--ink-soft);
          margin-top:6px;
        }
        input[type="range"]{
          accent-color:var(--pillar);
          width:100%;
        }
        .rating-opts{
          display:flex;
          flex-direction:column;
          gap:6px;
        }
        .clear-filters{
          font-size:12.5px;
          color:var(--pillar-dark);
          font-weight:600;
          text-decoration:underline;
          margin-top:8px;
          display:inline-block;
          cursor:pointer;
        }
        @media (max-width:900px){
          .sidebar{display:none;position:static;}
          .sidebar.open{display:block;margin-bottom:26px;}
        }

        .active-chips{
          display:flex;
          flex-wrap:wrap;
          gap:8px;
          margin-bottom:18px;
        }
        .active-chip{
          display:flex;
          align-items:center;
          gap:8px;
          background:var(--paper-2);
          border-radius:100px;
          padding:7px 8px 7px 14px;
          font-size:13px;
          color:var(--ink);
        }
        .active-chip .x{
          width:18px;
          height:18px;
          border-radius:50%;
          background:var(--card);
          display:flex;
          align-items:center;
          justify-content:center;
          font-size:11px;
          cursor:pointer;
          line-height:1;
        }

        .results-grid{
          display:grid;
          grid-template-columns:repeat(3,1fr);
          gap:18px;
        }
        @media (max-width:1100px){
          .results-grid{grid-template-columns:repeat(2,1fr);}
        }
        @media (max-width:640px){
          .results-grid{grid-template-columns:1fr;}
        }

        .r-card{
          background:var(--card);
          border:1px solid var(--line);
          border-radius:22px;
          overflow:hidden;
          box-shadow:0 10px 26px -22px rgba(21,48,43,0.22);
          transition:box-shadow .2s, transform .2s, border-color .2s;
          cursor:pointer;
          display:flex;
          flex-direction:column;
          min-height:100%;
        }
        .r-card:hover{
          box-shadow:var(--shadow);
          transform:translateY(-3px);
          border-color:rgba(21,48,43,0.18);
        }
        .r-head{
          display:flex;
          justify-content:space-between;
          align-items:flex-start;
          gap:12px;
          padding:18px 18px 12px;
        }
        .r-avatar-wrap{
          width:72px;
          height:72px;
          border-radius:20px;
          background:linear-gradient(145deg, rgba(63,102,82,0.95), rgba(87,98,143,0.92));
          overflow:hidden;
          display:flex;
          align-items:center;
          justify-content:center;
          flex-shrink:0;
        }
        .r-avatar-wrap img{
          width:100%;
          height:100%;
          object-fit:cover;
          display:block;
        }
        .r-avatar{
          font-family:'Fraunces',serif;
          font-size:26px;
          color:#fff;
          font-weight:600;
          letter-spacing:.03em;
        }
        .r-count{
          font-family:'IBM Plex Mono',monospace;
          font-size:11.5px;
          letter-spacing:.06em;
          text-transform:lowercase;
          color:var(--ink-soft);
          white-space:nowrap;
          padding-top:8px;
        }
        .r-body{
          padding:0 18px 18px;
          display:flex;
          flex-direction:column;
          gap:10px;
          flex:1;
        }
        .r-name h4{
          font-family:'Fraunces',serif;
          font-weight:600;
          font-size:20px;
          margin:0;
          line-height:1.12;
          letter-spacing:-0.01em;
        }
        .r-name .role{
          font-size:12.5px;
          color:var(--ink-soft);
          margin-top:5px;
          line-height:1.35;
        }
        .r-badges{
          display:flex;
          gap:6px;
          flex-wrap:wrap;
          margin-top:2px;
        }
        .r-badge{
          font-size:10.5px;
          padding:4px 9px;
          border-radius:999px;
          background:var(--paper-2);
          color:var(--ink-soft);
        }
        .r-bio{
          font-size:13.5px;
          color:var(--ink-soft);
          line-height:1.5;
          min-height:42px;
        }
        .r-services{
          display:flex;
          gap:6px;
          flex-wrap:wrap;
        }
        .r-services .r-badge{
          background:rgba(63,102,82,0.08);
        }
        .r-meta{
          display:flex;
          justify-content:space-between;
          align-items:center;
          gap:10px;
          padding-top:10px;
          margin-top:2px;
          border-top:1px dashed var(--line);
          font-size:11.5px;
          color:var(--ink-soft);
        }
        .r-rating{
          color:var(--marigold-dark);
          font-weight:600;
          white-space:nowrap;
        }
        .r-footer{
          display:flex;
          flex-direction:column;
          gap:12px;
          border-top:1px dashed var(--line);
          padding:12px 18px 18px;
          margin-top:auto;
        }
        .r-price{
          font-family:'IBM Plex Mono',monospace;
          font-size:13px;
          line-height:1.25;
          width:100%;
        }
        .r-price .from{
          font-size:9.5px;
          color:var(--ink-soft);
          display:block;
          font-family:'Inter',sans-serif;
          margin-bottom:2px;
          text-transform:uppercase;
          letter-spacing:.08em;
        }
        .r-actions{
          display:flex;
          gap:8px;
          width:100%;
        }
        .r-actions .sl-btn{
          flex:1;
          justify-content:center;
          padding:10px 12px;
          font-size:11.8px;
          min-width:0;
          border-radius:999px;
        }
        .r-actions .sl-btn:first-child{
          background:transparent;
          color:var(--ink);
          border-color:rgba(21,48,43,0.14);
        }
        .r-actions .sl-btn:last-child{
          background:var(--ink);
          color:var(--paper);
          border-color:var(--ink);
        }
        .r-actions .sl-btn:last-child:hover{
          background:var(--forest-dark);
          transform:none;
          box-shadow:none;
        }
        @media (max-width:640px){
          .r-head{padding:16px 16px 10px;}
          .r-body{padding:0 16px 16px;}
          .r-footer{padding:10px 16px 16px;}
          .r-avatar-wrap{width:64px;height:64px;border-radius:18px;}
          .r-name h4{font-size:18px;}
        }

      `}</style>

      <div
        style={{
          background: 'var(--paper)',
          color: 'var(--ink)',
          fontFamily: "'Inter', sans-serif",
          minHeight: '100vh',
        }}
      >
        <div className="page-head">
          <div className="sl-wrap">
            <div className="page-head-row">
              <div>
                <div className="crumbs">
                  <Link to="/">Home</Link>
                  <span>/</span>
                  <Link to={`/pillar/${breadcrumbPillar.slug}`}>{breadcrumbPillar.name}</Link>
                  <span>/</span>
                  <span>{breadcrumbSpeciality}</span>
                </div>
                <span className="eyebrow">Meet a few of them</span>
                <h1>{filteredPractitioners.length} {listingTitle} available</h1>
              </div>
              <div className="head-controls">
                <button
                  className="sl-btn sl-btn-ghost filter-toggle"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                >
                  Filters
                </button>
                <select className="sortsel" defaultValue="Recommended">
                  <option value="Recommended">Sort: Recommended</option>
                  <option value="Highest">Highest rated</option>
                  <option value="Low">Price: Low to high</option>
                  <option value="High">Price: High to low</option>
                  <option value="Experienced">Most experienced</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="sl-wrap">
          <div className="listing-layout">
            <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
              <div className="filter-group">
                <h3>Service Type</h3>
                {availableServiceTypes.length > 0 ? availableServiceTypes.map((serviceType) => (
                  <label key={serviceType} className="filter-row">
                    <span>
                      <input
                        type="checkbox"
                        checked={selectedServiceTypes.includes(serviceType)}
                        onChange={() => toggleArrayItem(setSelectedServiceTypes, serviceType)}
                      />
                      {serviceType}
                    </span>
                  </label>
                )) : ALL_SERVICE_TYPES.map((serviceType) => (
                  <label key={serviceType} className="filter-row">
                    <span>
                      <input
                        type="checkbox"
                        checked={selectedServiceTypes.includes(serviceType)}
                        onChange={() => toggleArrayItem(setSelectedServiceTypes, serviceType)}
                      />
                      {serviceType}
                    </span>
                  </label>
                ))}
              </div>

              <div className="filter-group">
                <h3>Mode</h3>
                <label className="filter-row">
                  <span>
                    <input
                      type="checkbox"
                      checked={selectedModes.includes('Online')}
                      onChange={() => toggleArrayItem(setSelectedModes, 'Online')}
                    />
                    Online
                  </span>
                </label>
                <label className="filter-row">
                  <span>
                    <input
                      type="checkbox"
                      checked={selectedModes.includes('Offline')}
                      onChange={() => toggleArrayItem(setSelectedModes, 'Offline')}
                    />
                    Offline
                  </span>
                </label>
              </div>

              {/* <div className="filter-group">
                <h3>City</h3>
                {availableCities.length > 0 ? availableCities.map((city) => (
                  <label key={city} className="filter-row">
                    <span>
                      <input
                        type="checkbox"
                        checked={selectedCities.includes(city)}
                        onChange={() => toggleArrayItem(setSelectedCities, city)}
                      />
                      {city}
                    </span>
                  </label>
                )) : (
                  <div style={{ color: 'var(--ink-soft)', fontSize: 13 }}>No city filters available</div>
                )}
              </div> */}

              <div className="filter-group">
                <h3>Price per session (Max)</h3>
                <input
                  type="range"
                  min="0"
                  max="3000"
                  step="100"
                  value={priceLimit}
                  onChange={(e) => setPriceLimit(Number(e.target.value))}
                />
                <div className="price-range">
                  <span>₹0</span>
                  <span>₹{priceLimit.toLocaleString('en-IN')}</span>
                  <span>₹3,000+</span>
                </div>
              </div>

              <div className="filter-group">
                <h3>Rating</h3>
                <div className="rating-opts">
                  <label className="filter-row">
                    <span>
                      <input
                        type="checkbox"
                        checked={minRating === 4.5}
                        onChange={() => setMinRating(minRating === 4.5 ? 0 : 4.5)}
                      />
                      ★ 4.5 &amp; above
                    </span>
                  </label>
                  <label className="filter-row">
                    <span>
                      <input
                        type="checkbox"
                        checked={minRating === 4.0}
                        onChange={() => setMinRating(minRating === 4.0 ? 0 : 4.0)}
                      />
                      ★ 4.0 &amp; above
                    </span>
                  </label>
                </div>
              </div>

              <div
                className="clear-filters"
                onClick={() => {
                  setSelectedServiceTypes(ALL_SERVICE_TYPES);
                  setSelectedModes(ALL_MODES);
                  setSelectedCities([]);
                  setPriceLimit(3000);
                  setMinRating(0);
                }}
              >
                Clear all filters
              </div>
            </aside>

            <div>
              <div className="active-chips">
                {selectedServiceTypes.map((serviceType) => (
                  <div key={serviceType} className="active-chip">
                    {serviceType}
                    <span className="x" onClick={() => toggleArrayItem(setSelectedServiceTypes, serviceType)}>×</span>
                  </div>
                ))}
                {selectedModes.map((mode) => (
                  <div key={mode} className="active-chip">
                    {mode}
                    <span className="x" onClick={() => toggleArrayItem(setSelectedModes, mode)}>×</span>
                  </div>
                ))}
                {selectedCities.map((city) => (
                  <div key={city} className="active-chip">
                    {city}
                    <span className="x" onClick={() => toggleArrayItem(setSelectedCities, city)}>×</span>
                  </div>
                ))}
                {priceLimit < 3000 && (
                  <div className="active-chip">
                    Up to ₹{priceLimit.toLocaleString('en-IN')}
                    <span className="x" onClick={() => setPriceLimit(3000)}>×</span>
                  </div>
                )}
                {minRating > 0 && (
                  <div className="active-chip">
                    {minRating}+ Rating
                    <span className="x" onClick={() => setMinRating(0)}>×</span>
                  </div>
                )}
              </div>

              <div className="results-grid">
                {loading ? (
                  <div style={{ padding: '24px', gridColumn: '1 / -1', textAlign: 'center', color: 'var(--ink-soft)' }}>
                    Loading instructors...
                  </div>
                ) : filteredPractitioners.length === 0 ? (
                  <div style={{ padding: '24px', gridColumn: '1 / -1', textAlign: 'center', color: 'var(--ink-soft)' }}>
                    No instructors found matching criteria.
                  </div>
                ) : (
                  filteredPractitioners.map((card) => {
                    const bookService = pickBookService(card);
                    const priceLabel = getPriceLabel(card.price);
                    return (
                      <div
                        className="r-card"
                        key={card.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate(`/public/instructor-profile/${card.slug}`, {
                          state: {
                            sourcePillar: breadcrumbPillar,
                            sourceSpeciality: breadcrumbSpeciality,
                            sourceName: card.name,
                          },
                        })}
                        onKeyDown={(event) => onCardKeyDown(event, card.slug)}
                      >
                        <div className="r-head">
                          <div className="r-avatar-wrap">
                            {card.avatar ? (
                              <img
                                src={card.avatar}
                                alt={card.name}
                                loading="lazy"
                                onError={(e) => {
                                  const img = e.currentTarget;
                                  img.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="r-avatar">{card.initials}</div>
                            )}
                          </div>
                          <div className="r-count">{card.serviceCount} specialities</div>
                        </div>

                        <div className="r-body">
                          <div className="r-name">
                            <h4>{card.name}</h4>
                            <div className="role">{card.role}</div>
                          </div>

                          <div className="r-badges">
                            <span className="r-badge">{card.city}</span>
                            <span className="r-badge">{card.modeLabel}</span>
                            <span className="r-badge">{card.experience}</span>
                          </div>

                          <div className="r-bio" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {card.bio}
                          </div>

                          {card.serviceTypes.length > 0 && (
                            <div className="r-services">
                              {card.serviceTypes.slice(0, 2).map((type) => (
                                <span key={type} className="r-badge">{type}</span>
                              ))}
                            </div>
                          )}

                          <div className="r-meta">
                            <span className="r-rating">★ {card.rating.toFixed(1)} · {card.reviews} reviews</span>
                            <span>{card.responseTime}</span>
                          </div>
                        </div>

                        <div className="r-footer">
                          <div className="r-price">
                            <span className="from">Starting from</span>
                            {priceLabel} / session
                          </div>
                          {getServiceSummary(card.services) && (
                            <div style={{ fontSize: 12, color: 'var(--ink-soft)', lineHeight: 1.4 }}>
                              {getServiceSummary(card.services)}
                            </div>
                          )}
                          <div className="r-actions">
                            <button
                              className="sl-btn sl-btn-ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/public/instructor-profile/${card.slug}`, {
                                  state: {
                                    sourcePillar: breadcrumbPillar,
                                    sourceSpeciality: breadcrumbSpeciality,
                                    sourceName: card.name,
                                  },
                                });
                              }}
                            >
                              View profile
                            </button>
                            {bookService ? (
                              <button
                                className="sl-btn sl-btn-primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/booking/${card.slug}/step1?classId=${bookService.id}`);
                                }}
                              >
                                Book session
                              </button>
                            ) : (
                              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, color: 'var(--ink-soft)', padding: '10px 6px' }}>
                                Booking unavailable
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SpecialityListing;
